import React, { useEffect, useMemo, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import {useParams} from 'react-router-dom';
import {StyledInvoicePageContainer, StyledInvoicePageContainerSpace} from './StyledInvoicePage';
import NotFoundPage from '../NotFoundPage';
import PageContainer from '../../components/PageContainer';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import SpinSmall from '../../components/SpinSmall';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader/BalancePageHeader';
import InvoiceDocumentCard from '../../components/pages/InvoicePage/InvoiceDocumentCard';
import InvoiceOverview from '../../components/pages/InvoicePage/InvoiceOverview';
import {invoicesActions} from '../../state/actions/invoice.actions';
import {expenseActions} from '../../state/actions/expense.actions';
import routes from '../../routes/routes.json';
import {expenseTypesConstants, invoicePaymentStatusConstants} from '../../constants';
import {objectHelpers, systemHelpers} from '../../utils/helpers';
import {firebaseEvents} from '../../snippets/firebase';


const defaultSelectedTransaction = { id: undefined, external: false };

const InvoicePage = () => {
  const [invoice, setInvoice] = useState(null);
  const [pageLoading, setPageLoading] = useState({loading: true, isExist: true});
  const [neighborIds, setNeighborIds] = useState({ prev: null, next: null });
  const [transactions, setTransactions] = useState({ searched: [], suggested: [] });
  const [approvals, setApprovals] = useState([]);

  const dispatch = useDispatch();
  const [selectedTransaction, setSelectedTransaction] = useState(defaultSelectedTransaction);

  const invoicesListUrl = useSelector(state => state.invoice.invoicesListUrl);
  const invoicesListQuery = useSelector(state => state.invoice?.invoicesListQuery);

  const [t] = useTranslation(['main']);

  const uriParams = useParams();

  const invoiceId = useMemo(() => uriParams?.invoiceId, [uriParams]);

  const {breadcrumbs, documentTitle} = useMemo(() => {
    const createdDate = invoice?.created_at;
    const title = !pageLoading?.isExist && !pageLoading?.loading
      ? t('elementNotFound', { name: t('invoice') })
      : createdDate
        ? moment(createdDate).format('DD MMM YYYY')
        : '';

    return {
      breadcrumbs: [
        {title: t('navigation.invoices'), href: invoicesListUrl || routes.invoicesList},
        {title}
      ],
      documentTitle: `${t('pageTitles.invoiceDetails')} ${title ?? ''}`,
    }
  }, [invoice, invoicesListUrl, t, pageLoading]);

  const finishLoading = (isExist = true) => setPageLoading({...pageLoading, isExist, loading: false});

  const loadInvoiceDetails = (invoiceId) => {
    dispatch(invoicesActions.getInvoice(
      invoiceId,
      (invoice) => {
        setInvoice(invoice);
        finishLoading();
      },
      () => finishLoading(false)
    ));
  }

  const loadInvoiceApprovals = (invoiceId) => {
    dispatch(invoicesActions.getInvoiceApproval({
      id: invoiceId,
      successCallback: (response) => setApprovals(response?.results || []),
      errorCallback: () => setApprovals([])
    }));
  }

  const loadInvoiceNeighbors = (invoiceId) => {
    dispatch(invoicesActions.getInvoiceNeighbours({
      id: invoiceId,
      query: invoicesListQuery,
      successCallback: (data) => {
        setNeighborIds({
          prev: data?.previous_invoice_id,
          next: data?.next_invoice_id
        });
      }
    }));
  }

  useEffect(() => {
    if (!objectHelpers.isValidValue(invoiceId)) return;

    setPageLoading({...pageLoading, loading: true});

    loadInvoiceDetails(invoiceId);
    loadInvoiceNeighbors(invoiceId);
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (invoice?.approval_status) {
      loadInvoiceApprovals(invoiceId);
    } else if (approvals.length) {
      setApprovals([]);
    }
  }, [invoice?.approval_status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selected transaction when invoice changes
  useEffect(() => {
    if (!objectHelpers.arraysIsEqual(selectedTransaction, defaultSelectedTransaction)) {
      setSelectedTransaction(defaultSelectedTransaction);
    }
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditInvoice = ({data, successCallback, errorCallback} = {}) => {
    if (!invoiceId) return;

    dispatch(invoicesActions.updateInvoice(
      invoiceId,
      data,
      (updatedInvoice) => {
        setInvoice({...updatedInvoice, file_url: invoice.file_url})
        successCallback?.(updatedInvoice);
        systemHelpers.logEvent(firebaseEvents.INVOICES_EDIT_INVOICE_DETAILS);
      },
      (resp) => errorCallback?.(resp)
    ));
  }

  const handleUpdateInvoice = (invoice, additionalData = {}) => {
    if (invoice) {
      setInvoice(invoice);
    } else {
      dispatch(invoicesActions.getInvoice(invoiceId, setInvoice));
    }

    if (additionalData?.approvals) {
      setApprovals(additionalData?.approvals);
    }
  }

  const handlePaidOutside = ({paid = true, successCallback} = {}) => {
    if (!invoice) return;

    dispatch(invoicesActions.payInvoiceOutside({
      id: invoice.id,
      paid,
      successCallback: (invoice) => {
        successCallback?.(invoice);
        setInvoice(invoice);
      },
      errorCallback: finishLoading
    }));
  }

  const handleLinkAndUpdateExpense = (expenseId) => {
    if (!invoiceId) return;

    dispatch(invoicesActions.linkExpense({
      id: invoiceId,
      data: {expense_id: expenseId},
      successCallback: (invoice) => {
        setInvoice(invoice);
        dispatch(expenseActions.updateExpense({
          id: expenseId,
          data: {expense_type: expenseTypesConstants[invoice.type]},
          successCallback: finishLoading,
          errorCallback: finishLoading
        }));
      },
      errorCallback: finishLoading
    }));
  };

  const handleMatchTransaction = (selectedTransaction) => {
    setSelectedTransaction(selectedTransaction);

    if (!invoiceId) return;

    if (!selectedTransaction.id && !selectedTransaction.external) return;

    setPageLoading(prevState => ({...prevState, loading: true}));

    if (selectedTransaction.external) {
      handlePaidOutside({ successCallback: finishLoading });
    } else {
      const transactionsList = [...transactions.suggested, ...transactions.searched];
      const expenseId = transactionsList.find(transaction => transaction.id === selectedTransaction.id)?.expense_id;
      if (!expenseId) {
        finishLoading();
        return;
      }

      const isCurrentlyPaidOutside = invoice.payment_status === invoicePaymentStatusConstants.PAID_OUTSIDE;

      if (isCurrentlyPaidOutside) {
        handlePaidOutside({
          paid: false,
          successCallback: () => handleLinkAndUpdateExpense(expenseId)
        });
      } else {
        handleLinkAndUpdateExpense(expenseId);
      }
    }
  }

  const {isExist, loading} = pageLoading;

  return (
    <PageContainer>
      <PageDocumentDetails title={documentTitle} />
      <BalancePageHeader breadcrumbs={breadcrumbs} />
      <SpinSmall spinning={loading}>
        <StyledInvoicePageContainer>

          {isExist ? (
            <StyledInvoicePageContainerSpace
              align='start'
              size='large'
            >
              <InvoiceDocumentCard
                invoice={invoice}
                neighborIds={neighborIds}
              />
              <InvoiceOverview
                approvals={approvals}
                invoice={invoice}
                neighborIds={neighborIds}
                onEdit={handleEditInvoice}
                onUpdate={handleUpdateInvoice}
                selectedTransaction={selectedTransaction}
                transactions={transactions}
                onSelectedTransactionChange={handleMatchTransaction}
                onTransactionsChange={setTransactions}
              />
            </StyledInvoicePageContainerSpace>
          ) : !loading && <NotFoundPage />}

        </StyledInvoicePageContainer>
      </SpinSmall>
    </PageContainer>
  );
}

export default InvoicePage;
